import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Check, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Control Center — the accounts worth looking at, most irregular first.
 *
 * The stats panel answers "does this account look odd?" about somebody a
 * moderator already thought to search for, which is the wrong way round for
 * finding trouble: it needs a suspicion before it can confirm one. The nightly
 * scanAccountsForIrregularities job scores every recently-created account and
 * writes the results here, so the queue arrives without anyone guessing a
 * username first.
 *
 * "Mark as good" DELETES the flag rather than storing a cleared copy. There is
 * no archive of vouched-for accounts, because a list of people a moderator has
 * already decided about is not something anybody goes back to read.
 *
 * What is kept is one number on the USER document: the score at the moment it
 * was cleared. The scan re-flags that account only if it scores HIGHER later —
 * so "good" holds until something new actually happens, rather than until the
 * next nightly run hands back the same faces.
 *
 * Nothing on this screen penalises anyone. It sorts a list; a human decides.
 */

interface Props {
  dark: boolean;
  /** Open the full moderation panel on this user. */
  onInspect?: (userId: string, username: string) => void;
}

interface Flag {
  id: string;
  userId: string;
  username: string;
  name: string;
  score: number;
  reasons: string[];
  /** Age at SCAN time. Do not display it: see liveAgeDays. */
  ageDays: number | null;
  /** Raw account creation time, written by newer scans. */
  createdMs?: number | null;
  scannedAt?: any;
}

/**
 * How old the account is NOW.
 *
 * The list used to print `ageDays` straight from the flag document, which is
 * computed inside the scan and frozen there — so every flagged account showed
 * the age it was when it was last scanned rather than its age today, and a
 * queue that had not been rescanned for a while reported the same stale number
 * for everyone in it.
 *
 * Two sources, because flags written before this fix have no createdMs:
 *   • createdMs, when present, gives the real answer;
 *   • otherwise the frozen ageDays is brought forward by however long ago the
 *     scan ran, which reconstructs the right number from what old documents
 *     already store — no rescan needed for the queue to start reading correctly.
 */
function liveAgeDays(f: Flag): number | null {
  const DAY = 86400000;
  if (typeof f.createdMs === 'number' && f.createdMs > 0) {
    return Math.max(0, Math.floor((Date.now() - f.createdMs) / DAY));
  }
  if (typeof f.ageDays !== 'number') return null;
  const scanned = f.scannedAt?.toMillis?.() ?? null;
  if (!scanned) return f.ageDays;
  return f.ageDays + Math.max(0, Math.floor((Date.now() - scanned) / DAY));
}

export function ControlCenter({ dark, onInspect }: Props) {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [sortBy, setSortBy] = useState<'score' | 'recent'>('score');
  const [error, setError] = useState<string | null>(null);

  // Live, not a one-shot read.
  //
  // The flagged-accounts BADGE on the Legion remote has always been an
  // onSnapshot, so it ticked upward the moment a scan flagged something — while
  // this list, the thing a moderator actually works from, was a single getDocs
  // that went stale the instant it resolved. A moderator could sit on a queue
  // reading "4 accounts" with the badge beside it saying 11, and the only way
  // to reconcile them was the manual Rescan button, which re-runs the whole
  // scan just to re-read a collection.
  //
  // Subscribing puts the list on the same footing as the badge: both now follow
  // the collection, so new accounts land in the queue as they are flagged and
  // cleared ones leave it on every open moderator's screen at once.
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { getDbSafe } = await import('../services/firebaseConfig');
        const { collection, query, orderBy, limit, onSnapshot } = await import('firebase/firestore');
        const db = await getDbSafe();
        if (cancelled) return;

        unsubscribe = onSnapshot(
          query(collection(db, 'flagged_accounts'), orderBy('score', 'desc'), limit(100)),
          snap => {
            // Both orderings are done in memory off one query, so switching
            // between them costs nothing and needs no second composite index.
            setFlags(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Flag[]);
            setLoading(false);
          },
          err => {
            setError(err?.message || 'Could not load the queue');
            setLoading(false);
          },
        );
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Could not load the queue');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; unsubscribe?.(); };
  }, []);

  const markGood = async (flag: Flag) => {
    // Optimistic: the row leaves the queue immediately. A moderator clearing a
    // run of accounts should not wait on a round trip each time.
    const previous = flags;
    setFlags(prev => prev.filter(f => f.id !== flag.id));
    try {
      const { getDbSafe, getAuthSafe } = await import('../services/firebaseConfig');
      const { doc, deleteDoc, updateDoc } = await import('firebase/firestore');
      const db = await getDbSafe();
      const auth = await getAuthSafe();

      // The score at clear time lives on the user document, so the next scan
      // knows this account was vouched for and only re-raises it if things get
      // worse. Written first: if the delete fails the marker is harmless, while
      // deleting without it would let tonight's scan hand the account straight
      // back.
      await updateDoc(doc(db, 'users', flag.userId), {
        modClearedScore: flag.score,
        modClearedAt: new Date().toISOString(),
        modClearedBy: auth.currentUser?.uid || '',
      }).catch(() => { /* non-fatal: the delete below still removes it now */ });

      await deleteDoc(doc(db, 'flagged_accounts', flag.id));
    } catch (e: any) {
      setFlags(previous);
      toast.error(e?.message || 'Could not mark that account');
    }
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const res: any = await httpsCallable(getFunctions(), 'rescanAccountsNow')({});
      toast.success(`Scanned ${res?.data?.scanned ?? 0} accounts`, {
        description: `${res?.data?.flagged ?? 0} carry irregularities`,
      });
      // No reload: the subscription above already pushes whatever the scan
      // wrote. Rescan now only triggers the scan itself.
    } catch (e: any) {
      toast.error(e?.message || 'Rescan failed');
    } finally {
      setRescanning(false);
    }
  };

  const shown = [...flags].sort((a, b) => (
    sortBy === 'score'
      ? b.score - a.score
      // "Most recent" means youngest ACCOUNT, not most recently scanned: a
      // moderator scanning this list is looking for accounts that just arrived.
      : (liveAgeDays(a) ?? 1e9) - (liveAgeDays(b) ?? 1e9)
  ));

  const muted = dark ? '#9ca3af' : '#6b7280';
  const fg = dark ? '#ffffff' : '#111111';
  const border = dark ? '#333' : '#e5e5e5';

  const severityColor = (score: number) =>
    score >= 60 ? '#E74C3C' : score >= 30 ? '#F39C12' : '#888';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p style={{ fontSize: 13, fontWeight: 800, color: fg }}>Control Center</p>
          <p style={{ fontSize: 11, color: muted }}>
            {flags.length} account{flags.length === 1 ? '' : 's'} with irregularities
          </p>
        </div>
        <button
          onClick={rescan}
          disabled={rescanning}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg disabled:opacity-50"
          style={{ fontSize: 11, fontWeight: 700, border: `1px solid ${border}`, color: fg }}
        >
          <RefreshCw className={`w-3 h-3 ${rescanning ? 'animate-spin' : ''}`} />
          {rescanning ? 'Scanning…' : 'Rescan'}
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        {([['score', 'Most irregularities'], ['recent', 'Most recent']] as ['score' | 'recent', string][])
          .map(([val, label]) => (
            <button
              key={val}
              onClick={() => setSortBy(val)}
              className="px-3 py-1.5 rounded-lg"
              style={{
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: sortBy === val ? (dark ? '#333' : '#eee') : 'transparent',
                color: sortBy === val ? fg : muted,
              }}
            >
              {label}
            </button>
          ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin opacity-40" /></div>
      ) : error ? (
        <p style={{ fontSize: 12, color: '#E74C3C' }}>{error}</p>
      ) : shown.length === 0 ? (
        <p style={{ fontSize: 12, color: muted }}>
          Nothing irregular in the queue. If this is the first run, use Rescan.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {shown.map(f => (
            <div key={f.id} className="rounded-xl p-3" style={{ border: `1px solid ${border}` }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="px-1.5 py-0.5 rounded"
                      style={{ fontSize: 10, fontWeight: 800, color: '#fff', backgroundColor: severityColor(f.score) }}
                    >
                      {f.score}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: fg }} className="truncate">
                      {f.username || f.name || f.userId}
                    </span>
                  </div>
                  {(() => {
                    const age = liveAgeDays(f);
                    if (age === null) return null;
                    return (
                      <p style={{ fontSize: 10, color: muted, marginTop: 2 }}>
                        {age === 0 ? 'joined today' : `${age}d old`}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {onInspect && (
                    <button
                      onClick={() => onInspect(f.userId, f.username)}
                      className="px-2 py-1 rounded-lg"
                      style={{ fontSize: 11, fontWeight: 700, border: `1px solid ${border}`, color: fg }}
                    >
                      Inspect
                    </button>
                  )}
                  <button
                    onClick={() => markGood(f)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ fontSize: 11, fontWeight: 700, backgroundColor: 'rgba(15,157,88,0.15)', color: '#0F9D58' }}
                  >
                    <Check className="w-3 h-3" /> Good
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1 mt-2">
                {(f.reasons || []).map((r, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 shrink-0" style={{ marginTop: 2, color: severityColor(f.score) }} />
                    <span style={{ fontSize: 11, color: muted }}>{r}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="flex items-start gap-1.5 mt-3" style={{ fontSize: 10, color: muted }}>
        <ShieldCheck className="w-3 h-3 shrink-0" style={{ marginTop: 1 }} />
        <span>
          Heuristics, not proof. Nothing here restricts an account on its own. Marking one
          good removes it from the queue for good, unless it later scores higher than it
          does now.
        </span>
      </p>
    </div>
  );
}
